import { useState, useEffect, useCallback } from 'react';
import { RoomEvent, Track, TrackPublication } from 'livekit-client';

/**
 * A robust hook that synchronizes a list of SUBSCRIBED remote audio tracks.
 * It uses a declarative synchronization function triggered by subscription events,
 * ensuring the returned list is always an accurate and render-ready mirror of the room's state.
 *
 * @param {import('livekit-client').Room} room - The LiveKit Room object.
 * @returns {TrackPublication[]} An array of remote TrackPublication objects that are ready to be rendered.
 */
export const useRemoteAudioTracks = (room) => {
  const [remoteTracks, setRemoteTracks] = useState([]);

  // Synchronization logic for audio tracks
  const synchronizeTracks = useCallback(() => {
    if (!room) return;

    // Get all potential audio publications from the LiveKit room object.
    const allAudioPublications = [];
    room.remoteParticipants.forEach(participant => {
      participant.audioTrackPublications.forEach(publication => {
        if (publication.source === Track.Source.Microphone || publication.source === Track.Source.ScreenShareAudio) {
          allAudioPublications.push(publication);
        }
      });
    });

    setRemoteTracks(currentLocalTracks => {
      const allAudioSids = new Set(allAudioPublications.map(p => p.trackSid));
      const localSids = new Set(currentLocalTracks.map(p => p.trackSid));

      // Only add tracks that are new, subscribed, and ready
      const additionList = allAudioPublications.filter(p =>
        !localSids.has(p.trackSid) && p.isSubscribed && p.track
      );
      // Remove tracks that are no longer present or unsubscribed
      const removalList = currentLocalTracks.filter(p =>
        !allAudioSids.has(p.trackSid) || !p.isSubscribed
      );

      if (additionList.length === 0 && removalList.length === 0) {
        return currentLocalTracks;
      }

      let newTrackList = [...currentLocalTracks];
      const removalSids = new Set(removalList.map(p => p.trackSid));
      newTrackList = newTrackList.filter(p => !removalSids.has(p.trackSid));
      newTrackList = [...newTrackList, ...additionList];

      console.log('Synchronizing SUBSCRIBED remote audio tracks.', {
        added: additionList.length,
        removed: removalList.length,
      });

      return newTrackList;
    });
  }, [room]);

  useEffect(() => {
    if (!room) {
      setRemoteTracks([]);
      return;
    }
    synchronizeTracks();
    room.on(RoomEvent.TrackSubscribed, synchronizeTracks);
    room.on(RoomEvent.TrackUnsubscribed, synchronizeTracks);
    room.on(RoomEvent.TrackUnpublished, synchronizeTracks);
    room.on(RoomEvent.ParticipantDisconnected, synchronizeTracks);

    // Redundancy interval in case events are missed
    const redundancyInterval = setInterval(() => {
      synchronizeTracks();
    }, 2000); // every 2 seconds

    return () => {
      // Clear redundancy interval
      if (redundancyInterval) {
        clearInterval(redundancyInterval);
      }
      // Clean up listeners
      if (!room) return;
      room.off(RoomEvent.TrackSubscribed, synchronizeTracks);
      room.off(RoomEvent.TrackUnsubscribed, synchronizeTracks);
      room.off(RoomEvent.TrackUnpublished, synchronizeTracks);
      room.off(RoomEvent.ParticipantDisconnected, synchronizeTracks);
    };
  }, [room, synchronizeTracks]);

  return remoteTracks;
};
